// NPS / CSAT CALCULÉ — on colle les notes exportées de l'outil d'enquête.
//
// « Calculer » demande le score au serveur sans rien enregistrer (aperçu) ;
// « Enregistrer » l'ajoute à l'historique comme une mesure suivie (« NPS » ou
// « CSAT »), qui peut ensuite servir de métrique de succès à une hypothèse.
// Le calcul vit sur le serveur : l'écran et la mesure enregistrée lisent la
// même règle.
import { useId, useState, type FormEvent } from 'react';
import { ApiRequestError } from '../api/client';
import { Button, ErrorBanner, Field, Select } from '../components/ui';
import { useScoreMeasure, type ScoreKind, type ScoreResult } from './measures';

const HELP: Record<ScoreKind, string> = {
  nps: 'Notes de 0 à 10 à « Recommanderiez-vous… ? ». NPS = % de promoteurs (9-10) − % de détracteurs (0-6).',
  csat: 'Notes de 1 à 5 à « Êtes-vous satisfait… ? ». CSAT = % de réponses 4 ou 5.',
};

const fmt = (n: number) => n.toLocaleString('fr-FR', { maximumFractionDigits: 1 });

export function ScoreForm({ projectId, onDone }: { projectId: string; onDone: () => void }) {
  const score = useScoreMeasure(projectId);
  const [kind, setKind] = useState<ScoreKind>('nps');
  const [scores, setScores] = useState('');
  const [recordedOn, setRecordedOn] = useState('');
  const [preview, setPreview] = useState<ScoreResult | null>(null);
  const kindId = useId();
  const scoresId = useId();
  const today = new Date().toISOString().slice(0, 10);

  async function calculate() {
    try {
      setPreview((await score.mutateAsync({ kind, scores, preview: true })).result);
    } catch {
      setPreview(null);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await score.mutateAsync({ kind, scores, ...(recordedOn ? { recordedOn } : {}) });
      onDone();
    } catch {
      /* rendu par le bandeau */
    }
  }

  return (
    <form className="zy-card measure-form" onSubmit={submit}>
      <p className="card-lede">
        Collez les notes exportées de votre outil d’enquête (une par ligne, ou séparées par des virgules).
        Le score devient une mesure suivie, avec sa courbe.
      </p>
      <div className="measure-form-grid">
        <div className="zy-field">
          <label className="zy-field__label" htmlFor={kindId}>Score</label>
          <Select id={kindId} value={kind}
            onChange={(e) => { setKind(e.target.value as ScoreKind); setPreview(null); }}>
            <option value="nps">NPS (0 à 10)</option>
            <option value="csat">CSAT (1 à 5)</option>
          </Select>
        </div>
        <Field
          label="Date de l’enquête"
          type="date"
          value={recordedOn}
          max={today}
          onChange={(e) => setRecordedOn(e.target.value)}
          hint="Vide = aujourd’hui"
        />
      </div>
      <div className="zy-field">
        <label className="zy-field__label" htmlFor={scoresId}>Notes</label>
        <textarea id={scoresId} className="zy-input" rows={5} value={scores}
          placeholder={kind === 'nps' ? '9\n10\n7\n3\n…' : '5\n4\n2\n…'}
          onChange={(e) => { setScores(e.target.value); setPreview(null); }} />
        <span className="zy-field__aide">{HELP[kind]}</span>
      </div>

      {preview ? (
        <p className="score-preview" role="status">
          <strong>{preview.kind === 'nps' ? `NPS ${preview.value > 0 ? '+' : ''}${preview.value}` : `CSAT ${fmt(preview.value)} %`}</strong>
          {' '}sur {preview.n} réponses
          {preview.kind === 'nps' && preview.promoters !== undefined
            ? ` · ${fmt(preview.promoters)} % de promoteurs, ${fmt(preview.passives ?? 0)} % de passifs, ${fmt(preview.detractors ?? 0)} % de détracteurs`
            : ''}
          {preview.outOfScale + preview.ignored > 0 ? ` · ${preview.outOfScale + preview.ignored} valeur(s) écartée(s) hors échelle` : ''}
          {preview.fragile ? <span className="score-fragile"> · Moins de 30 réponses : à lire avec prudence.</span> : null}
        </p>
      ) : null}

      {score.error ? (
        <ErrorBanner>
          {score.error instanceof ApiRequestError ? score.error.message : 'Calcul impossible.'}
        </ErrorBanner>
      ) : null}
      <div className="measure-form-actions">
        <Button type="button" variant="ghost" loading={score.isPending && !preview} disabled={!scores.trim()} onClick={() => void calculate()}>
          Calculer
        </Button>
        <Button variant="accent" type="submit" loading={score.isPending && Boolean(preview)} disabled={!preview}>
          Enregistrer le score
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          Annuler
        </Button>
      </div>
    </form>
  );
}
